/**
 * API Endpoints
 */

// Auth
export const AUTH = {
  LOGIN: '/auth/login',
  REGISTER: '/auth/register',
  REFRESH: '/auth/refresh',
  LOGOUT: '/auth/logout',
  ME: '/auth/me',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/auth/reset-password',
  VERIFY_EMAIL: '/auth/verify-email',
} as const;

// Public
export const PUBLIC = {
  HEALTH: '/health',
  INFO: '/info',
  STATS: '/stats',
} as const;

// Plots (REINFO)
export const PLOTS = {
  LIST: '/plots',
  DETAIL: (id: number) => `/plots/${id}`,
  BY_CODE: (code: string) => `/plots/by-code/${code}`,
  CONCESSIONS: (id: number) => `/plots/${id}/concessions`,
  ENRICHMENTS: (id: number) => `/plots/${id}/enrichments`,
  SATELLITE: (id: number) => `/plots/${id}/satellite`,
  RELATIONS: (id: number) => `/plots/${id}/relations`,
} as const;

// Concessions (Catastro)
export const CONCESSIONS = {
  LIST: '/concessions',
  GEOJSON: '/concessions/geojson',
  DETAIL: (id: number) => `/concessions/${id}`,
  BY_CODE: (code: string) => `/concessions/by-code/${code}`,
  PLOTS: (id: number) => `/concessions/${id}/plots`,
  GEOMETRY: (id: number) => `/concessions/${id}/geometry`,
  AREA_ANALYSIS: (id: number) => `/concessions/${id}/area-analysis`,
  OVERLAPS: (id: number) => `/concessions/${id}/overlaps`,
  PREDIOS: (id: number) => `/concessions/${id}/predios`,
  COMUNIDADES: (id: number) => `/concessions/${id}/comunidades`,
  SERNANP: (id: number) => `/concessions/${id}/sernanp`,
  WATER: (id: number) => `/concessions/${id}/water`,
} as const;

// Owners
export const OWNERS = {
  DETAIL: (ruc: string) => `/owners/${ruc}`,
  PERSON: (dni: string) => `/owners/person/${dni}`,
  COMPANY: (ruc: string) => `/owners/company/${ruc}`,
  PLOTS: (ruc: string) => `/owners/${ruc}/plots`,
  CONCESSIONS: (ruc: string) => `/owners/${ruc}/concessions`,
  RELATIONS: (ruc: string) => `/owners/${ruc}/relations`,
  LOOKUP: '/owners/lookup',
} as const;

// Relations
export const RELATIONS = {
  BY_ADDRESS: '/relations/by-address',
  BY_COMPANY: (ruc: string) => `/relations/by-company/${ruc}`,
  BY_PHONE: (phone: string) => `/relations/by-phone/${phone}`,
  GRAPH: (ruc: string) => `/relations/graph/${ruc}`,
} as const;

// Layers
export const LAYERS = {
  OVERVIEW: '/layers',
  FORESTAL: '/layers/forestal',
  CERTIFICADOS: '/layers/certificados',
  LIBRE: '/layers/libre',
  INTERSECT: '/layers/intersect',
} as const;

// Enrichment
export const ENRICHMENT = {
  DATA: (sourceType: string, sourceId: number) => `/enrichments/${sourceType}/${sourceId}`,
  SATELLITE: (sourceType: string, sourceId: number) => `/enrichments/${sourceType}/${sourceId}/satellite`,
  PARSING: (sourceType: string, sourceId: number, type: string) => `/enrichments/${sourceType}/${sourceId}/parsing/${type}`,
  ANALYSIS: (sourceType: string, sourceId: number, type: string) => `/enrichments/${sourceType}/${sourceId}/analysis/${type}`,
} as const;

// Jobs
export const JOBS = {
  LIST: '/jobs',
  CREATE: '/jobs',
  BULK: '/jobs/bulk',
  DETAIL: (id: string) => `/jobs/${id}`,
  CANCEL: (id: string) => `/jobs/${id}`,
} as const;

// Search
export const SEARCH = {
  GLOBAL: '/search',
  BY_PHONE: (phone: string) => `/search/by-phone/${phone}`,
  BY_EMAIL: (email: string) => `/search/by-email/${encodeURIComponent(email)}`,
  BY_DNI: (dni: string) => `/search/by-dni/${dni}`,
  BY_BANK: (account: string) => `/search/by-bank/${account}`,
  BY_NAME: '/search/by-name',
} as const;

// Geo
export const GEO = {
  RADIUS: '/geo/radius',
  POLYGON: '/geo/polygon',
  NEAREST: '/geo/nearest',
  CLUSTER: '/geo/cluster',
} as const;

// Export
export const EXPORT = {
  CSV: '/export/csv',
  GEOJSON: '/export/geojson',
  SHAPEFILE: '/export/shapefile',
  EXCEL: '/export/excel',
  STATUS: (id: string) => `/export/${id}`,
  DOWNLOAD: (id: string) => `/export/${id}/download`,
} as const;

// Reports
export const REPORTS = {
  OWNER: (ruc: string) => `/reports/owner/${ruc}`,
  PLOT: (sourceType: string, sourceId: number) => `/reports/plot/${sourceType}/${sourceId}`,
  AREA: '/reports/area',
} as const;

// References
export const REFERENCES = {
  TYPE: (type: string) => `/references/${type}`,
} as const;

// User Data
export const USER_DATA = {
  FAVORITES: '/user/favorites',
  FAVORITE: (id: string) => `/user/favorites/${id}`,
  NOTES: '/user/notes',
  NOTE: (id: string) => `/user/notes/${id}`,
  IMAGES: '/user/images',
  IMAGE: (id: string) => `/user/images/${id}`,
  HISTORY: '/user/history',
} as const;

// Admin (excluded from main frontend, for reference)
export const ADMIN = {
  USERS: '/admin/users',
  USER: (uuid: string) => `/admin/users/${uuid}`,
  ROLES: '/admin/roles',
  PERMISSIONS: '/admin/permissions',
  AUDIT: '/admin/audit',
  USAGE: '/admin/usage',
} as const;

// Trees / Biogeology
export const TREES = {
  SPECIES: '/trees/species',
  SPECIES_DETAIL: (id: number) => `/trees/species/${id}`,
  ANALYZE: '/trees/analyze',
  ANALYZE_V2: '/trees/analyze/v2',
  REFORESTATION: '/trees/reforestation',
  JOBS: '/trees/jobs',
  INVENTORY: '/trees/inventory',
  INVENTORY_STATS: '/trees/inventory/stats',
  INVENTORY_GEOJSON: '/trees/inventory/geojson',
  INVENTORY_GPX: '/trees/inventory/export/gpx',
  PHOTOS_GEOJSON: '/trees/photos/geojson',
} as const;

// Geoanalysis
export const GEOANALYSIS = {
  STATUS: '/geoanalysis/status',
  ANALYZE_POINT: '/geoanalysis/analyze/point',
  HIERARCHICAL: '/geoanalysis/hierarchical',
  HIERARCHICAL_QUICK: '/geoanalysis/hierarchical/quick',
  HIERARCHICAL_RESULT: '/geoanalysis/hierarchical/result',
  HEATMAP: '/geoanalysis/heatmap',
  WEBODM_TRACK: '/geoanalysis/webodm/track',
  PHOTOS_STATS: '/geoanalysis/photos/stats',
  PHOTOS_RESULTS: '/geoanalysis/photos/results',
} as const;
